import React, { useRef, useCallback } from 'react';
import { gsap } from './gsapClient';
import { useReducedMotion } from './useReducedMotion';

interface MagneticProps {
    children: React.ReactNode;
    /** 0–1, fraction of the pointer offset the element follows (default 0.3) */
    strength?: number;
    className?: string;
    style?: React.CSSProperties;
}

/**
 * Magnetic — the wrapped element is gently pulled toward the pointer while
 * hovered and springs back on leave. Wrap buttons/pills, not large regions.
 *
 * Usage:
 *   <Magnetic strength={0.4}><button>Apply</button></Magnetic>
 */
export const Magnetic: React.FC<MagneticProps> = ({
    children,
    strength = 0.3,
    className,
    style,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const reduced = useReducedMotion();

    const onMove = useCallback(
        (e: React.PointerEvent) => {
            const el = ref.current;
            if (!el || reduced) return;
            const rect = el.getBoundingClientRect();
            const dx = e.clientX - (rect.left + rect.width / 2);
            const dy = e.clientY - (rect.top + rect.height / 2);
            gsap.to(el, {
                x: dx * strength,
                y: dy * strength,
                duration: 0.3,
                ease: 'power2.out',
            });
        },
        [strength, reduced]
    );

    const onLeave = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        gsap.to(el, { x: 0, y: 0, duration: 0.5, ease: 'power3.out' });
    }, []);

    return (
        <div
            ref={ref}
            className={className}
            style={{ display: 'inline-block', ...style }}
            onPointerMove={onMove}
            onPointerLeave={onLeave}
        >
            {children}
        </div>
    );
};
